<template>
  <div>
    <!-- Create step: no sidebar -->
    <game-builder-config-step
      v-if="currentStep === 'create'"
      :is-loading="isSaving"
      @save="onCreateGame"
    />

    <!-- Config step for existing game -->
    <game-builder-config-step
      v-else-if="currentStep === 'config'"
      :game="gameData"
      :is-loading="isSaving"
      @save="onSaveConfig"
    />

    <!-- Wizard steps with sidebar -->
    <content-with-sidebar
      v-else-if="gameData"
      class="cws-container cws-sidebar-right blog-wrapper"
    >
      <!-- Main content -->
      <div class="blog-detail-wrapper">
        <b-overlay
          :show="isLoading"
          rounded="sm"
        >
          <!-- Done step -->
          <b-card v-if="currentStep === 'done'">
            <h3 class="mb-1">
              Игра готова!
            </h3>
            <p>Все темы и вопросы заполнены.</p>
            <div class="d-flex" style="gap: 8px">
              <b-button
                variant="outline-secondary"
                @click="openConfigStep"
              >
                Конфигурация
              </b-button>
              <b-button
                variant="success"
                :disabled="isSaving"
                @click="onFinalize"
              >
                <span v-if="isSaving">
                  <b-spinner small />
                  Финализация...
                </span>
                <span v-else>Финализировать игру</span>
              </b-button>
              <b-button
                variant="primary"
                :disabled="isExporting"
                @click="onExportSiq"
              >
                <span v-if="isExporting">
                  <b-spinner small />
                  Сборка...
                </span>
                <span v-else>Собрать SIQ файл</span>
              </b-button>
              <b-button
                variant="outline-danger"
                @click="onDeleteGame"
              >
                Удалить игру
              </b-button>
            </div>
          </b-card>

          <template v-else>
            <div class="d-flex justify-content-end mb-50">
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="openConfigStep"
              >
                Конфигурация
              </b-button>
            </div>

            <!-- Progress bar -->
            <b-progress
              :value="progressPercent"
              :max="100"
              variant="primary"
              class="mb-2"
              style="height: 6px"
            />

            <!-- Topic step -->
            <game-builder-topic-step
              v-if="currentStep === 'topic'"
              :suggested-topic="suggestedTopic"
              :available-items="availableItems"
              :is-loading="isLoadingTopic"
              :is-saving="isSaving"
              :current-rt="currentRtObject"
              :game-type="gameData ? gameData.type : null"
              :show-format-picker="gameData && gameData.type === 'builder-music'"
              :question-format="currentRtObject.question_format || 'music'"
              @accepted="onTopicStepAccepted"
              @skipped="onTopicStepSkipped"
              @format-changed="onFormatChanged"
              @random-started="onRandomTopicStarted"
            />

            <!-- Frame step -->
            <game-builder-frame-step
              v-if="currentStep === 'frames'"
              :game-id="gameId"
              :rt-id="currentRtId"
              :current-rt="currentRtObject"
              :suggested-movie="suggestedMovie"
              :frames-source="framesSource"
              :is-loading-movie="isLoadingMovie"
              :suggestion-requested="movieSuggestionRequested"
              :is-saving="isSaving"
              @accepted="onQuestionAccepted"
              @skipped="onMovieSkipped"
              @question-deleted="onQuestionDeleted"
              @topic-removed="onTopicRemoved"
              @question-slot-changed="onQuestionSlotChanged"
              @suggest-picked="onMovieSuggestPicked"
            />

            <!-- Music question step -->
            <game-builder-music-question-step
              v-if="currentStep === 'music-question'"
              :game-id="gameId"
              :current-rt="currentRtObject"
              :suggestion="suggestedMusicQuestion"
              :is-loading-suggestion="isLoadingMusicQuestion"
              :suggestion-requested="musicSuggestionRequested"
              :is-saving="isSaving"
              @accepted="onMusicQuestionAccepted"
              @skipped="onMusicQuestionSkipped"
              @question-deleted="onQuestionDeleted"
              @question-updated="loadGame(gameId, true)"
              @question-slot-changed="onQuestionSlotChanged"
              @topic-removed="onTopicRemoved"
              @suggest-picked="onMusicSuggestPicked"
            />
          </template>
        </b-overlay>
      </div>

      <!-- Sidebar -->
      <template #sidebar>
        <game-builder-progress-sidebar
          :game-data="gameData"
          :current-rt-id="currentRtId"
          @rt-selected="onRtSelected"
        />
      </template>
    </content-with-sidebar>

    <!-- Global loading -->
    <div
      v-if="!gameData && currentStep !== 'create'"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BOverlay, BProgress, BSpinner,
} from 'bootstrap-vue'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ContentWithSidebar from '@core/layouts/components/content-with-sidebar/ContentWithSidebar.vue'
import router from '@/router'
import store from '@/store'
import gameBuilderStoreModule from './gameBuilderStoreModule'
import GameBuilderConfigStep from './GameBuilderConfigStep.vue'
import GameBuilderTopicStep from './GameBuilderTopicStep.vue'
import GameBuilderFrameStep from './GameBuilderFrameStep.vue'
import GameBuilderMusicQuestionStep from './GameBuilderMusicQuestionStep.vue'
import GameBuilderProgressSidebar from './GameBuilderProgressSidebar.vue'
import { isRandomFormat as isRandomFormatValue } from './gameBuilderMusicFormats'

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BProgress,
    BSpinner,
    ContentWithSidebar,
    GameBuilderConfigStep,
    GameBuilderTopicStep,
    GameBuilderFrameStep,
    GameBuilderMusicQuestionStep,
    GameBuilderProgressSidebar,
  },
  setup() {
    const STORE_MODULE_NAME = 'game-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const gameId = ref(router.currentRoute.params.id || null)
    const gameData = ref(null)
    const currentRtId = ref(null)
    // null до загрузки игры — показывается глобальный лоадер, а не экран настройки
    const currentStep = ref(null)
    const suggestedTopic = ref(null)
    const availableItems = ref(null)
    const suggestedMovie = ref(null)
    const suggestedMusicQuestion = ref(null)
    const isLoading = ref(false)
    const isSaving = ref(false)
    const isExporting = ref(false)
    const isLoadingTopic = ref(false)
    const isLoadingMovie = ref(false)
    const isLoadingMusicQuestion = ref(false)
    // Признак, что саджест вопроса/фильма был запрошен вручную (кнопкой) — управляет текстом пустого состояния
    const musicSuggestionRequested = ref(false)
    const movieSuggestionRequested = ref(false)

    const isRtComplete = rt => {
      if (!rt.topic_id) return false
      const required = rt.required_questions || 5
      return (rt.questions?.length || 0) >= required
    }

    const allRoundTopics = computed(() => {
      if (!gameData.value || !gameData.value.rounds) return []
      const result = []
      gameData.value.rounds.forEach(round => {
        if (round.topics) {
          round.topics.forEach(rt => {
            result.push({ ...rt, round_name: round.name, round })
          })
        }
      })
      return result
    })

    const currentRtObject = computed(() => allRoundTopics.value.find(
      rt => rt.id === currentRtId.value,
    ) || {})

    const progressPercent = computed(() => {
      const total = allRoundTopics.value.length
      if (!total) return 0
      const completed = allRoundTopics.value.filter(
        rt => isRtComplete(rt),
      ).length
      return Math.round((completed / total) * 100)
    })

    const fetchSuggestAutotopic = () => {
      isLoadingTopic.value = true
      suggestedTopic.value = null
      store.dispatch('game-builder/suggestAutotopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
      })
        .then(response => {
          const data = response.data.data || response.data
          suggestedTopic.value = data.autotopic || data
          availableItems.value = data.available_items || null
          isLoadingTopic.value = false
        })
        .catch(error => {
          isLoadingTopic.value = false
          if (error.response && error.response.status === 422) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Нет доступных автотем',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        })
    }

    const framesSource = ref(null)

    // constraints — ограничения ручного выбора (movie_id), передаются как params в запрос
    const fetchSuggestMovie = (constraints = null) => {
      movieSuggestionRequested.value = true
      isLoadingMovie.value = true
      suggestedMovie.value = null
      framesSource.value = null
      store.dispatch('game-builder/suggestMovie', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        params: constraints || undefined,
      })
        .then(response => {
          const data = response.data.data || response.data
          framesSource.value = data.frames_source || null
          if (data.movie) {
            suggestedMovie.value = {
              ...data.movie,
              frames: data.frames || [],
            }
          } else {
            suggestedMovie.value = data
          }
          isLoadingMovie.value = false
        })
        .catch(error => {
          isLoadingMovie.value = false
          if (error.response && error.response.status === 422) {
            toast({
              component: ToastificationContent,
              props: {
                title: error.response?.data?.message || 'Нет фильмов с кадрами для этой темы',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        })
    }

    const fetchSuggestMusicTopic = () => {
      isLoadingTopic.value = true
      suggestedTopic.value = null
      store.dispatch('game-builder/suggestMusicTopic', { gameId: gameId.value, rtId: currentRtId.value })
        .then(response => {
          const data = response.data.data || response.data
          suggestedTopic.value = data.autotopic || data
          availableItems.value = data.available_items || null
          isLoadingTopic.value = false
        })
        .catch(error => {
          isLoadingTopic.value = false
          if (error.response && error.response.status === 422) {
            toast({
              component: ToastificationContent,
              props: { title: 'Нет автотем для этого формата', icon: 'AlertTriangleIcon', variant: 'warning' },
            })
          }
        })
    }

    // constraints — ограничения ручного выбора (artist_id/side), передаются как params в запрос
    const fetchSuggestMusicQuestion = (constraints = null) => {
      musicSuggestionRequested.value = true
      isLoadingMusicQuestion.value = true
      suggestedMusicQuestion.value = null
      store.dispatch('game-builder/suggestMusicQuestion', {
        gameId: gameId.value, rtId: currentRtId.value, params: constraints || undefined,
      })
        .then(response => {
          suggestedMusicQuestion.value = response.data.data || response.data
          isLoadingMusicQuestion.value = false
        })
        .catch(error => {
          isLoadingMusicQuestion.value = false
          const message = error.response?.data?.message
          if (message) {
            toast({
              component: ToastificationContent,
              props: { title: message, icon: 'AlertTriangleIcon', variant: 'warning' },
            })
          }
        })
    }

    // Обёртка для @skipped: шаблон биндит без аргументов, иначе Vue передаст event первым аргументом в constraints
    const onMusicQuestionSkipped = () => {
      fetchSuggestMusicQuestion()
    }

    // Артист выбран вручную на шаге — перезапрашиваем предложение с этим ограничением
    const onMusicSuggestPicked = constraints => {
      fetchSuggestMusicQuestion(constraints)
    }

    // Открыть шаг для конкретной темы (тема не начата → выбор темы, начата → вопросы)
    const openRtStep = rt => {
      const isMusicGame = gameData.value.type === 'builder-music'
      if (!rt.topic_id) {
        currentStep.value = 'topic'
        if (isMusicGame) {
          const format = rt.question_format || 'music'
          if (!isRandomFormatValue(format)) {
            fetchSuggestMusicTopic()
          } else {
            suggestedTopic.value = null
            isLoadingTopic.value = false
          }
        } else {
          fetchSuggestAutotopic()
        }
      } else if (isMusicGame) {
        currentStep.value = 'music-question'
        if (isRtComplete(rt)) {
          // Заполненная тема — только просмотр/редактирование, саджест не нужен
          suggestedMusicQuestion.value = null
          isLoadingMusicQuestion.value = false
          musicSuggestionRequested.value = false
        } else {
          fetchSuggestMusicQuestion()
        }
      } else {
        currentStep.value = 'frames'
        if (isRtComplete(rt)) {
          suggestedMovie.value = null
          isLoadingMovie.value = false
          movieSuggestionRequested.value = false
        } else {
          fetchSuggestMovie()
        }
      }
    }

    const onRtSelected = rtId => {
      const rt = allRoundTopics.value.find(item => item.id === rtId)
      if (!rt) return
      currentRtId.value = rtId
      openRtStep(rt)
    }

    const advanceStep = () => {
      if (!gameData.value) {
        currentStep.value = 'create'
        return
      }

      // Остаёмся в выбранной теме, пока она не заполнена
      const current = allRoundTopics.value.find(rt => rt.id === currentRtId.value)
      if (current && !isRtComplete(current)) {
        openRtStep(current)
        return
      }

      // Текущая заполнена (или не выбрана) — следующая незаполненная после неё, по кругу
      const topics = allRoundTopics.value
      const startIndex = current ? topics.indexOf(current) + 1 : 0
      const rotated = topics.slice(startIndex).concat(topics.slice(0, startIndex))
      const next = rotated.find(rt => !isRtComplete(rt))

      if (!next) {
        currentStep.value = 'done'
        currentRtId.value = null
        return
      }

      currentRtId.value = next.id
      openRtStep(next)
    }

    // keepPosition: true — перезагрузка не является шагом мастера (смена слота, правка частей вопроса),
    // текущую тему покидать не нужно, данные шага обновятся реактивно через currentRtObject
    const loadGame = (id, keepPosition = false) => {
      isLoading.value = true
      store.dispatch('game-builder/loadGame', { id })
        .then(response => {
          gameData.value = response.data.data || response.data
          isLoading.value = false
          if (!keepPosition) {
            advanceStep()
          }
        })
        .catch(() => {
          isLoading.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка загрузки игры',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    // Init
    if (gameId.value) {
      loadGame(gameId.value)
    } else {
      currentStep.value = 'create'
    }

    // Event handlers

    const openConfigStep = () => {
      currentStep.value = 'config'
    }

    const formatLossLine = loss => {
      const topicName = loss.topic_name ? `«${loss.topic_name}»` : `слот ${loss.round_number}`
      if (loss.reason === 'questions_trimmed') {
        return `${loss.round_name}, ${topicName}: будут удалены вопросы (${loss.questions_count} шт.)`
      }
      return `${loss.round_name}, ${topicName}: тема будет удалена целиком (вопросов: ${loss.questions_count})`
    }

    const sendConfig = (payload, confirm = false) => {
      isSaving.value = true
      store.dispatch('game-builder/updateConfig', {
        id: gameId.value,
        payload: { ...payload, confirm },
      })
        .then(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Конфигурация сохранена',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          loadGame(gameId.value)
        })
        .catch(error => {
          isSaving.value = false
          const data = error.response?.data?.data

          if (error.response?.status === 422 && data?.requires_confirmation) {
            const lines = data.losses.map(formatLossLine).join('\n')
            if (window.confirm(`Изменение конфигурации удалит заполненный контент:\n\n${lines}\n\nПродолжить?`)) {
              sendConfig(payload, true)
            }
            return
          }

          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка сохранения конфигурации',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onSaveConfig = payload => {
      sendConfig(payload)
    }

    const onCreateGame = payload => {
      isSaving.value = true
      store.dispatch('game-builder/createGame', payload)
        .then(response => {
          const data = response.data.data || response.data
          gameData.value = data
          gameId.value = data.id
          isSaving.value = false
          router.replace({ name: 'game-builder', params: { id: data.id } })
          advanceStep()
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка создания игры',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicAccepted = autotopicId => {
      isSaving.value = true
      store.dispatch('game-builder/acceptAutotopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        autotopicId,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка принятия темы',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicSkipped = () => {
      fetchSuggestAutotopic()
    }

    const onFormatChanged = value => {
      isSaving.value = true
      store.dispatch('game-builder/setTopicFormat', {
        gameId: gameId.value, rtId: currentRtId.value, questionFormat: value,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => { isSaving.value = false })
    }

    const onMusicTopicAccepted = autotopicId => {
      isSaving.value = true
      store.dispatch('game-builder/startMusicTopic', {
        gameId: gameId.value, rtId: currentRtId.value, autotopicId,
      })
        .then(() => { isSaving.value = false; loadGame(gameId.value) })
        .catch(() => { isSaving.value = false })
    }

    const onRandomTopicStarted = name => {
      isSaving.value = true
      store.dispatch('game-builder/startMusicTopic', {
        gameId: gameId.value, rtId: currentRtId.value, name,
      })
        .then(() => { isSaving.value = false; loadGame(gameId.value) })
        .catch(() => { isSaving.value = false })
    }

    const onMusicQuestionAccepted = payload => {
      isSaving.value = true
      store.dispatch('game-builder/acceptMusicQuestion', {
        gameId: gameId.value, rtId: currentRtId.value, payload,
      })
        .then(() => { isSaving.value = false; loadGame(gameId.value) })
        .catch(error => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка сохранения вопроса',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    // Прокси: музыкальные игры принимают/пропускают тему через свои эндпоинты
    const onTopicStepAccepted = autotopicId => {
      if (gameData.value && gameData.value.type === 'builder-music') {
        onMusicTopicAccepted(autotopicId)
      } else {
        onTopicAccepted(autotopicId)
      }
    }

    const onTopicStepSkipped = () => {
      if (gameData.value && gameData.value.type === 'builder-music') {
        fetchSuggestMusicTopic()
      } else {
        fetchSuggestAutotopic()
      }
    }

    const onQuestionAccepted = payload => {
      isSaving.value = true
      store.dispatch('game-builder/acceptQuestion', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        payload,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка сохранения вопроса',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onMovieSkipped = () => {
      fetchSuggestMovie()
    }

    // Фильм выбран вручную на шаге — перезапрашиваем предложение с этим ограничением
    const onMovieSuggestPicked = constraints => {
      fetchSuggestMovie(constraints)
    }

    const onQuestionDeleted = questionId => {
      isSaving.value = true
      store.dispatch('game-builder/deleteQuestion', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        questionId,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления вопроса',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onQuestionSlotChanged = ({ questionId, slot }) => {
      isSaving.value = true
      store.dispatch('game-builder/moveQuestionSlot', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        questionId,
        slot,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value, true)
        })
        .catch(error => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка смены слота',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicRemoved = () => {
      isSaving.value = true
      store.dispatch('game-builder/removeTopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления темы',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onFinalize = () => {
      isSaving.value = true
      store.dispatch('game-builder/finalizeGame', { id: gameId.value })
        .then(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Игра финализирована',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.push({ name: 'games' })
        })
        .catch(error => {
          isSaving.value = false
          const msg = error.response?.data?.message || 'Ошибка финализации'
          toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onExportSiq = () => {
      isExporting.value = true
      store.dispatch('game-builder/exportSiq', { id: gameId.value })
        .then(() => store.dispatch('game-builder/downloadSiq', { id: gameId.value }))
        .then(response => {
          isExporting.value = false
          const blob = new Blob([response.data], { type: 'application/octet-stream' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const disposition = response.headers['content-disposition']
          const filename = disposition
            ? disposition.match(/filename="?(.+?)"?$/)?.[1] || `game-${gameId.value}.siq`
            : `game-${gameId.value}.siq`
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          toast({
            component: ToastificationContent,
            props: {
              title: 'SIQ файл скачан',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          isExporting.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка экспорта SIQ',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onDeleteGame = () => {
      if (!window.confirm('Удалить эту игру? Действие необратимо.')) return
      store.dispatch('game-builder/deleteGame', { id: gameId.value })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Игра удалена',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.push({ name: 'game-builder-list' })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    return {
      gameId,
      gameData,
      currentRtId,
      currentStep,
      suggestedTopic,
      availableItems,
      suggestedMovie,
      suggestedMusicQuestion,
      framesSource,
      isLoading,
      isSaving,
      isLoadingTopic,
      isLoadingMovie,
      isLoadingMusicQuestion,
      musicSuggestionRequested,
      movieSuggestionRequested,
      currentRtObject,
      progressPercent,
      onRtSelected,
      openConfigStep,
      onSaveConfig,
      onCreateGame,
      onTopicAccepted,
      onTopicSkipped,
      onFormatChanged,
      onTopicStepAccepted,
      onTopicStepSkipped,
      onRandomTopicStarted,
      fetchSuggestMusicQuestion,
      onMusicQuestionSkipped,
      onMusicSuggestPicked,
      onMusicQuestionAccepted,
      onQuestionAccepted,
      onMovieSkipped,
      onMovieSuggestPicked,
      onQuestionDeleted,
      onQuestionSlotChanged,
      onTopicRemoved,
      isExporting,
      onExportSiq,
      onFinalize,
      onDeleteGame,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
